import { Nullable, PartialNullable } from "../../Extensions";
import { StringHelper } from "../../Helpers/StringHelper";
import { IJNativeEvent } from "../../JNative";
import { PropertyChange } from "../../PropertyChange";
import { Property, PropertyBoolean, PropertyString } from "../../RadObject";
import { ElementRef } from "../ElementRef";
import { JsxFragment } from "../JsxFragment";
import { Button } from "./Button";
import { ControlRef } from "./ControlRef";
import { IPanel, Panel, PanelPropertyValues } from "./Panel";
import { NAMESPACE } from "./_Namespace";

import "./ValidationPanel.scss";

export type ValidationPanelPropertyValues =
    PanelPropertyValues
    & {
        shouldPluralizeValidationErrorText: boolean;
        shouldScrollToTop: boolean;
        titleTextWhenValidationError: string;
        validationErrors: any[];
    };

export type ValidationPanelProperties = {
    [name in keyof ValidationPanelPropertyValues]: Property<ValidationPanelPropertyValues[name]>
};

export interface IValidationPanel extends IPanel, ValidationPanelProperties
{
}

export class ValidationPanel extends Panel implements IValidationPanel
{
    // #region Static

    public static override classNamespace = NAMESPACE + "ValidationPanel";
    public static override className = "ValidationPanel";

    // #region Overrides

    public static override createPropertyDefaults(): PartialNullable<ValidationPanelPropertyValues>
    {
        return {
            ...super.createPropertyDefaults(),
            ...{
                shouldPluralizeValidationErrorText: true,
                shouldScrollToTop: true,
                titleTextWhenValidationError: "Validation error",
                visible: false,
            }
        };
    }

    // #endregion

    // #endregion

    // #region Instance

    // #region Global Variables

    protected _buttonCloseRef!: ControlRef<Button>;
    protected _listElementRef!: ElementRef;
    protected _titleElementRef!: ElementRef;

    // #endregion

    // #region Properties

    public shouldPluralizeValidationErrorText!: PropertyBoolean;
    public shouldScrollToTop!: PropertyBoolean;
    public titleTextWhenValidationError!: PropertyString;
    public validationErrors!: Property<any[]>;

    // #endregion

    // #region Constructor

    constructor(propertyValues?: PartialNullable<ValidationPanelPropertyValues>)
    {
        super(propertyValues);

        this.addCssClass("rad-control-validation-panel");

        // Trigger Property Changes
        this.titleTextWhenValidationError.triggerChange();
        this.validationErrors.triggerChange();
    }

    // #endregion

    // #region Public Methods

    public setErrorMessages(messages: Nullable<string[]>, type?: string, forceVisible?: boolean)
    {
        if (messages)
            messages = messages.filter(item => !!item);

        const
            titleTextWhenValidationError = this.titleTextWhenValidationError.get();

        type = type ? type : "Validation error";

        this._listElementRef.current.empty();

        if (StringHelper.isNullOrEmpty(titleTextWhenValidationError) && type === "Validation error")
            this._titleElementRef.current.hide();
        else
        {
            this._titleElementRef.current.show();

            this._titleElementRef.current.text(
                type === "Validation error"
                    ? titleTextWhenValidationError + (messages && messages.length > 1 && this.shouldPluralizeValidationErrorText.get() ? "s" : StringHelper.empty)
                    : type
            );
        }

        if (messages?.length)
        {
            messages.forEach(item =>
            {
                this._listElementRef.current.append(StringHelper.formatWithHtmlEncode("<li>{0}</li>", item));
            });

            this.show();
        }
        else
            this.hide();

        if (forceVisible)
            this.show();
    }

    // #endregion

    // #region Protected Methods

    protected _shouldPluralizeValidationErrorTextChanged(change: PropertyChange<boolean>)
    {
        /* Intentionally empty */
    }
    protected _shouldScrollToTopChanged(change: PropertyChange<boolean>)
    {
        /* Intentionally empty */
    }
    protected _titleTextWhenValidationErrorChanged(change: PropertyChange<string>)
    {
        /* Intentionally empty */
    }
    protected _validationErrorsChanged(change: PropertyChange<any[]>)
    {
        this.setErrorMessages(
            change.currentValue
                ? change.currentValue.map(item => item.errorMessage)
                : []
        );
    }

    // #endregion

    // #region Overrides

    protected override __initializeProperties(propertyValues: ValidationPanelPropertyValues)
    {
        super.__initializeProperties(propertyValues);

        this._buttonCloseRef = new ControlRef();
        this._titleElementRef = new ElementRef();
        this._listElementRef = new ElementRef();

        this.shouldPluralizeValidationErrorText = new PropertyBoolean(this, propertyValues.shouldPluralizeValidationErrorText, this._shouldPluralizeValidationErrorTextChanged);
        this.shouldScrollToTop = new PropertyBoolean(this, propertyValues.shouldScrollToTop, this._shouldScrollToTopChanged);
        this.titleTextWhenValidationError = new PropertyString(this, propertyValues.titleTextWhenValidationError, this._titleTextWhenValidationErrorChanged);
        this.validationErrors = new Property<any[]>(this, "System.Object, System.Private.CoreLib", propertyValues.validationErrors, this._validationErrorsChanged);
    }

    protected override _bindEvents()
    {
        super._bindEvents();

        this._buttonCloseRef.current?.on("click", { self: this }, this.onButtonCloseClicked);
    }
    protected override _createElementHtml(children?: JsxFragment | string | (JsxFragment | string | undefined)[])
    {
        return (
            <div ref={this._containerElementRef}>
                <h3 ref={this._titleElementRef}></h3>
                <ul ref={this._listElementRef}></ul>
                {children ?? this._createChildrenHtml()}
                <Button ref={this._buttonCloseRef} cssClass="rad-action-close-validation-panel" inline={true} showLabel={false} svgKey="icon-cross" text="Close" />
            </div>
        );
    }

    public override show()
    {
        super.show();

        if (this.shouldScrollToTop.get())
        {
            const
                page = this.page.get();

            if (page)
                page.scrollToTop();
        }
    }

    // #endregion

    // #region Event Handlers

    protected onButtonCloseClicked(evt: IJNativeEvent)
    {
        const
            self = evt.data.self as this;

        self.hide();
    }

    // #endregion

    // #endregion
}